import { ApiError, type HttpClient } from "@dl/web-core-js/api";

export type Currency = "CAD" | "USD";

/**
 * One money bucket — the sale lines or the return lines of a group. Values
 * arrive already signed: a refund bucket holds positive magnitudes.
 *
 * `line_total` is Σ price × shipped qty, before freight, discount and tax.
 * Nothing on a bucket is "net": it only sees one side, so it cannot subtract
 * the other. Read billed via `billedOf()` in `api/figures`.
 */
export interface InvoiceTotals {
  order_type?: number;
  quantity: number;
  /** The second unit count — see `Figures.items_billed`. */
  items: number;
  shipping: number;
  discount: number;
  commission: number;
  line_total: number;
}

/** Per-line cost enrichment: COGS (in CAD), royalty, shipping, advertising. */
export interface CostDTO {
  cogs?: number;
  royalty?: number;
  shipping?: number;
  advertising?: number;
  advertising_sales?: number;
}

/**
 * Invoiced money for a slice of time — by client, by currency, or overall.
 *
 * `sales` and `refunds` are kept apart rather than netted, because "what did we
 * invoice" and "what came back" are different questions and the dashboard
 * answers the first.
 */
/**
 * The named sales figures for one scope, straight off the server.
 *
 * `net` is sent, not derived here: it is computed once in `InvoiceFigures` and
 * shipped, which is the whole point of the object. The browser used to
 * re-implement this arithmetic and drifted from the Java by four money
 * definitions and three unit definitions.
 *
 * Units have no net. A refund line needs a quantity to hold a price, so a
 * transport credit or an early-payment discount each count as "one unit
 * returned" — units are billed under either basis.
 */
export interface Figures {
  billed: number;
  discounts: number;
  returns: number;
  net: number;
  units_billed: number;
  /**
   * The second way to count units. Which question each answers depends on the
   * grouping, decided server-side in `InvoiceFigures`:
   *
   * - client / currency / company: `units_*` counts what was sold (a kit is
   *   one, its contents nothing); `items_*` counts the SKUs that shipped (the
   *   kit line nothing, each of its contents one).
   * - part: `units_*` is every line of the SKU, alone or inside a kit — what
   *   left the building; `items_*` is the SKU sold on its own.
   *
   * Neither counts a second carton (`…A` / `…AU` part numbers).
   */
  items_billed: number;
}

/**
 * Both scopes, sent together.
 *
 * `all` is every invoice except voids, computed server-side from the buckets,
 * so it is on every aggregate. `issued` is only those SIGM has numbered
 * (`inv_status = 'F'`, facturé) — the rest are shipped or waiting to ship and
 * not yet billed. It is **null when the query behind the aggregate was not
 * grouped by status**: the buckets cannot answer that scope, and null says so
 * rather than substituting `all`. What is not yet invoiced is `all.billed −
 * issued.billed`; there is no separate "excluded" figure to keep in step.
 */
export interface FiguresByScope {
  all: Figures;
  issued: Figures | null;
}

export interface InvoiceAggregateDTO {
  client?: SigmClientDTO | null;
  currency?: Currency;
  date_from?: string;
  date_to?: string;
  sales?: InvoiceTotals | null;
  refunds?: InvoiceTotals | null;
  cost?: CostDTO | null;
  parts?: InvoicePartAggregateDTO[];
  /** On every aggregate the server sends. */
  figures?: FiguresByScope | null;
}

/** One window of the batched endpoint, echoing the key it was asked with. */
export interface InvoiceWindowDTO {
  key: string;
  from: string;
  to: string;
  by_client: InvoiceAggregateDTO[];
  by_currency: InvoiceAggregateDTO[];
}

/** Ordered (booked) volume for a slice of time — the counterpart to invoiced. */
export interface OrderAggregateDTO {
  currency?: Currency;
  date_from?: string;
  date_to?: string;
  ordered_quantity: number;
  ordered_value: number;
  shipped_quantity: number;
  cancelled_quantity: number;
  order_count: number;
}

/** One window of the batched bookings endpoint, echoing the key it was asked with. */
export interface OrderWindowDTO {
  key: string;
  from: string;
  to: string;
  by_currency: OrderAggregateDTO[];
}

/** What a batched endpoint is asked: the caller's name for a window and its dates. */
export interface WindowRequest {
  key: string;
  from: string;
  to: string;
}

/**
 * The ERP client record. Field names carry the SIGM `cli_` prefix on the wire —
 * they are not the usual `id`/`name` and typing them that way silently drops
 * every row.
 */
export interface SigmClientDTO {
  cli_id: number;
  cli_no?: string | null;
  cli_name1?: string | null;
  cli_currency?: Currency | null;
  cli_active?: boolean;
  cli_email?: string | null;
  cli_dscnt?: number | null;
  cli_last_ord_dt?: string | null;
}

/** One client's invoiced totals for a SKU over a period. */
export interface InvoicePartAggregateDTO {
  client?: SigmClientDTO | null;
  currency?: Currency | null;
  part?: { id?: number; no?: string | null; description1?: string | null } | null;
  /** Convenience mirror of part.id for the margin return filter. */
  part_id?: number;
  totals?: InvoiceTotals | null;
  cost?: CostDTO | null;
}

/** CAD/USD conversion rates, from the currency config. */
export interface GlobalFX {
  currencies: Array<{ currency: Currency; rate: number }>;
}

export function fxRate(fx: GlobalFX | undefined, currency: Currency | null | undefined): number {
  if (!fx || !currency) return 1;
  return fx.currencies.find((entry) => entry.currency === currency)?.rate ?? 1;
}

/** `total + shipping − discount` — the figure the Vaadin chart plotted. */
/** Billed less the discount. Returns are NOT subtracted — they are in the other bucket. */
export function billedLessDiscount(totals: InvoiceTotals | null | undefined): number {
  if (!totals) return 0;
  return totals.line_total + totals.shipping - totals.discount;
}

export interface Period {
  from: string;
  to: string;
}

/**
 * A period something simply had no invoices in.
 *
 * The single-entity aggregate endpoints answer 404 when nothing matches, which
 * reads as "no such client" but actually means "no sales in that period". The
 * drill-down asks for twelve months at once through `Promise.all`, so one quiet
 * month rejected the whole batch and blanked the chart, the breakdown and the
 * product list together. The current year always contains months that have not
 * happened yet, so that was every client, every time.
 *
 * Zero is the honest answer, and the list endpoints already give it: they return
 * an empty array rather than 404 for exactly this situation.
 */
function emptyAggregate(period: Period, currency?: Currency): InvoiceAggregateDTO {
  return {
    date_from: period.from,
    date_to: period.to,
    ...(currency ? { currency } : {}),
    parts: [],
  };
}

/** Resolves 404 to an empty period; every other failure still propagates. */
async function zeroOnNotFound(
  request: Promise<InvoiceAggregateDTO>,
  period: Period,
  currency?: Currency,
): Promise<InvoiceAggregateDTO> {
  try {
    return await request;
  } catch (failure) {
    if (failure instanceof ApiError && failure.status === 404) {
      return emptyAggregate(period, currency);
    }
    throw failure;
  }
}

export class ReportingClient {
  constructor(private readonly http: HttpClient) {}

  /** Per-client aggregates for a period — the sales-analysis row source. */
  /**
   * Several windows in one round trip — each with the same per-client and
   * per-currency aggregates `aggregateByClient` / `aggregateByCurrency` return.
   */
  aggregateWindows(windows: WindowRequest[]): Promise<InvoiceWindowDTO[]> {
    return this.http.post<InvoiceWindowDTO[]>("/reporting/invoice/aggregate/windows", windows);
  }

  /**
   * Per-currency invoiced figures for several windows — no client rows, no
   * margin. `by_client` comes back empty.
   */
  aggregateWindowsByCurrency(windows: WindowRequest[]): Promise<InvoiceWindowDTO[]> {
    return this.http.post<InvoiceWindowDTO[]>("/reporting/invoice/aggregate/windows/currency", windows);
  }

  /** Per-currency ordered figures for several windows. */
  bookingsForWindowsByCurrency(windows: WindowRequest[]): Promise<OrderWindowDTO[]> {
    return this.http.post<OrderWindowDTO[]>("/reporting/order/aggregate/windows/currency", windows);
  }

  /** Per-currency figures for each of the last N calendar months, current month last. */
  monthlyByCurrency(months = 12): Promise<InvoiceWindowDTO[]> {
    return this.http.get<InvoiceWindowDTO[]>("/reporting/invoice/aggregate/monthly", { query: { months } });
  }

  aggregateByClient(period: Period): Promise<InvoiceAggregateDTO[]> {
    return this.http.get<InvoiceAggregateDTO[]>("/reporting/invoice/aggregate/period/client", {
      query: { date_from: period.from, date_to: period.to },
    });
  }

  /** One client's aggregate, including its per-part rows. */
  aggregateForClient(clientId: number, period: Period): Promise<InvoiceAggregateDTO> {
    return zeroOnNotFound(
      this.http.get<InvoiceAggregateDTO>(
        `/reporting/invoice/aggregate/period/client/${clientId}`,
        { query: { date_from: period.from, date_to: period.to } },
      ),
      period,
    );
  }

  /** Per-currency aggregates for a period — the totals rows. */
  aggregateByCurrency(period: Period): Promise<InvoiceAggregateDTO[]> {
    return this.http.get<InvoiceAggregateDTO[]>("/reporting/invoice/aggregate/period/currency", {
      query: { date_from: period.from, date_to: period.to },
    });
  }

  globalFx(): Promise<GlobalFX> {
    return this.http.get<GlobalFX>("/currency/exchange/rate/globalfx");
  }

  /** Invoiced totals for one currency over a period. */
  invoicedForCurrency(currency: Currency, period: Period): Promise<InvoiceAggregateDTO> {
    return zeroOnNotFound(
      this.http.get<InvoiceAggregateDTO>(
        `/reporting/invoice/aggregate/period/currency/${currency}`,
        { query: { date_from: period.from, date_to: period.to } },
      ),
      period,
      currency,
    );
  }

  /** Invoiced totals for one SKU in one currency over a period. */
  invoicedForPartCurrency(
    partNo: string,
    currency: Currency,
    period: Period,
  ): Promise<InvoiceAggregateDTO> {
    // Same 404-means-zero contract: a SKU with no sales in a given year is a
    // legitimate empty, and the product sales tab asks for several years at once.
    return zeroOnNotFound(
      this.http.get<InvoiceAggregateDTO>(
        `/reporting/invoice/aggregate/period/part/${encodeURIComponent(partNo)}/currency/${currency}`,
        { query: { date_from: period.from, date_to: period.to } },
      ),
      period,
      currency,
    );
  }

  /** Per-client invoiced rows for one SKU over a period. */
  partRowsByClient(partNo: string, period: Period): Promise<InvoicePartAggregateDTO[]> {
    return this.http.get<InvoicePartAggregateDTO[]>(
      `/reporting/invoice/part-rows/period/part/${encodeURIComponent(partNo)}/client`,
      { query: { date_from: period.from, date_to: period.to } },
    );
  }

  /** Booked totals for one currency over a period. */
  orderedForCurrency(currency: Currency, period: Period): Promise<OrderAggregateDTO> {
    return this.http.get<OrderAggregateDTO>(
      `/reporting/order/aggregate/period/currency/${currency}`,
      { query: { date_from: period.from, date_to: period.to } },
    );
  }

  /** Invoiced sales totals over a period — the services KPI denominator. */
  salesTotals(period: Period): Promise<InvoiceAggregateDTO> {
    return this.http.get<InvoiceAggregateDTO>("/reporting/invoice/aggregate/period/totals/sales", {
      query: { date_from: period.from, date_to: period.to },
    });
  }

  /** Per-client invoiced rows for a set of SKUs over a period. */
  partRowsByParts(partNos: string[], period: Period): Promise<InvoicePartAggregateDTO[]> {
    return this.http.get<InvoicePartAggregateDTO[]>("/reporting/invoice/part-rows/period/parts", {
      query: { part_nos: partNos, date_from: period.from, date_to: period.to },
    });
  }

  /** The sales workbook — the finance-report xlsx. Months go in the body. */
  documentSales(options: {
    year: number;
    months: MonthName[];
    clientId?: number | null;
    includeTotal: boolean;
    includeClients: boolean;
    includeProducts: boolean;
    includeRefunds: boolean;
    includeInvoices: boolean;
  }) {
    return this.http.job("/reporting/document/sales", {
      query: {
        year: options.year,
        ...(options.clientId ? { client_id: options.clientId } : {}),
        include_total: options.includeTotal,
        include_clients: options.includeClients,
        include_products: options.includeProducts,
        include_refunds: options.includeRefunds,
        include_invoices: options.includeInvoices,
      },
      body: options.months,
    });
  }

  /** Sales-rep commissions PDF for one year (optionally a month range). */
  documentCommission(year: number, months: MonthName[]) {
    return this.http.job("/reporting/document/commission", {
      query: { year },
      body: months,
    });
  }

  /** One designer's royalty statement PDF. */
  documentRoyalty(designerId: number, year: number, months: MonthName[]) {
    return this.http.job(`/reporting/document/royalty/${designerId}`, {
      query: { year },
      body: months,
    });
  }

  /** COGS workbook over an accounting period. */
  documentCogs(dateFrom: string, dateTo: string) {
    return this.http.job("/product/cogs/document/cogs", {
      query: { date_from: dateFrom, date_to: dateTo },
    });
  }
}

/** Jackson serializes `java.time.Month` by name. */
export const MONTH_NAMES = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
] as const;
export type MonthName = (typeof MONTH_NAMES)[number];

/** The Vaadin dialogs required selected months to be gap-free. */
export function areMonthsConsecutive(months: MonthName[]): boolean {
  const ordinals = months.map((month) => MONTH_NAMES.indexOf(month)).sort((a, b) => a - b);
  return ordinals.every((value, index) => index === 0 || ordinals[index - 1]! + 1 === value);
}

/** `YYYY-MM-DD` in local time — the format the period params expect. */
export function isoDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

export function monthToDate(today = new Date()): Period {
  return { from: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to: isoDate(today) };
}

export function yearToDate(today = new Date()): Period {
  return { from: isoDate(new Date(today.getFullYear(), 0, 1)), to: isoDate(today) };
}

/**
 * Exactly `weeks` complete Monday–Sunday weeks, the last one ending last
 * Sunday: the current week, still in progress, is never included. The same
 * window as the server's `DateRanges.lastCompleteWeeks`, so a figure on screen
 * and the KPI it is compared with cover the same days.
 */
export function lastCompleteWeeks(weeks: number, today = new Date()): Period {
  const sunday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  sunday.setDate(sunday.getDate() - (((sunday.getDay() + 6) % 7) + 1));
  const monday = new Date(sunday);
  monday.setDate(monday.getDate() - (weeks * 7 - 1));
  return { from: isoDate(monday), to: isoDate(sunday) };
}
